import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ManualLayout, ManualSection, ManualSubheading, ManualNote, scrollToManualSection } from '@redpanda/ui';

/**
 * 使用手冊：內容在 locales/{zh,en,ja}/manual.json（三語），這裡只負責排版
 * 區塊：p 段落、list 清單（ordered 有序）、terms「名稱：說明」、sub 小標題、note 提示框；文字裡的 **粗體** 會轉成粗體
 */
type Block =
  | { p: string }
  | { list: string[]; ordered?: boolean }
  | { terms: Array<[string, string]> }
  | { sub: string }
  | { note: { title: string; text: string } };

interface Manual {
  title: string;
  tocTitle: string;
  sections: Array<{ id: string; title: string; blocks: Block[] }>;
}

// 導覽頁面 → 章節（App.tsx 記錄 lastActivePage）
const navigationToSectionMap: Record<string, string> = {
  '/projects': 'projectManagement',
  '/tasks': 'tasks',
  '/timesheets': 'timesheets',
  '/receivables': 'receivables',
  '/profitability': 'profitability',
};

const List: React.FC<{ items: React.ReactNode[]; ordered?: boolean }> = ({ items, ordered }) => {
  const Tag = ordered ? 'ol' : 'ul';
  return (
    <Tag className={`${ordered ? 'list-decimal' : 'list-disc'} list-outside ml-5 space-y-1.5`}>
      {items.map((item, index) => <li key={index}>{item}</li>)}
    </Tag>
  );
};

/** 「名稱：說明」清單（取代原本的彩色狀態膠囊與彩色左邊框區塊） */
const Terms: React.FC<{ items: Array<[string, string]> }> = ({ items }) => (
  <dl className="divide-y divide-line border border-line rounded-lg bg-surface">
    {items.map(([term, desc]) => (
      <div key={term} className="grid grid-cols-[8rem_1fr] gap-3 px-4 py-2.5">
        <dt className="font-medium text-fg">{term}</dt>
        <dd>{desc}</dd>
      </div>
    ))}
  </dl>
);

const B: React.FC<{ children: React.ReactNode }> = ({ children }) => <strong className="font-medium text-fg">{children}</strong>;

/** 「**粗體：**說明」→ 粗體＋說明 */
const Rich: React.FC<{ text: string }> = ({ text }) => (
  <>{text.split(/(\*\*[^*]+\*\*)/).map((part, i) => (part.startsWith('**') ? <B key={i}>{part.slice(2, -2)}</B> : part))}</>
);

const renderBlock = (block: Block, index: number) => {
  if ('p' in block) return <p key={index}><Rich text={block.p} /></p>;
  if ('list' in block) return <List key={index} ordered={block.ordered} items={block.list.map((item, i) => <Rich key={i} text={item} />)} />;
  if ('terms' in block) return <Terms key={index} items={block.terms} />;
  if ('sub' in block) return <ManualSubheading key={index} className={index <= 1 ? 'mt-0' : undefined}>{block.sub}</ManualSubheading>;
  return <ManualNote key={index} title={block.note.title}><Rich text={block.note.text} /></ManualNote>;
};

const ManualPage: React.FC = () => {
  const { t } = useTranslation('manual');
  const manual = t('content', { returnObjects: true }) as Manual;
  const sections = manual.sections;
  const location = useLocation();
  const [initialScrollTarget, setInitialScrollTarget] = useState<string | null>(null);

  // 進入時捲到對應章節：?from= 參數 → 網址 #章節（分享的連結）→ 最後停留的系統頁面 → referrer
  useEffect(() => {
    const fromSystem = new URLSearchParams(location.search).get('from');
    let target = (fromSystem && navigationToSectionMap[fromSystem]) || '';

    if (!target && location.hash) {
      const hashTarget = location.hash.substring(1);
      if (sections.some(section => section.id === hashTarget)) target = hashTarget;
    }
    if (!target) {
      const lastActivePage = sessionStorage.getItem('lastActivePage');
      if (lastActivePage && navigationToSectionMap[lastActivePage]) target = navigationToSectionMap[lastActivePage];
    }
    if (!target && document.referrer) {
      try {
        const referrerPath = new URL(document.referrer).pathname;
        const match = Object.keys(navigationToSectionMap).find(path => referrerPath === path || referrerPath.startsWith(`${path}/`));
        if (match) target = navigationToSectionMap[match];
      } catch {
        // URL 解析失敗時略過
      }
    }
    setInitialScrollTarget(target || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.hash]);

  // 章節與目錄同一次 render 產生，下一個畫面捲過去即可（不輪詢）
  useEffect(() => {
    if (!initialScrollTarget) return;
    const frame = requestAnimationFrame(() => scrollToManualSection(initialScrollTarget));
    return () => cancelAnimationFrame(frame);
  }, [initialScrollTarget]);

  return (
    <ManualLayout title={manual.title} tocTitle={manual.tocTitle} toc={sections.map(section => ({ id: section.id, label: section.title }))}>
      {sections.map(section => (
        <ManualSection key={section.id} id={section.id} title={section.title}>
          {section.blocks.map(renderBlock)}
        </ManualSection>
      ))}
    </ManualLayout>
  );
};

export default ManualPage;
